import React, { useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import {
  SyntheticAthlete,
  PROPOSED_METRICS_EVALUATION,
} from './syntheticDashboardData'
import { SyntheticTeamTrackingView } from './SyntheticTeamTrackingView'
import { SyntheticSportPerformanceView } from './SyntheticSportPerformanceView'
import { SyntheticIndividualPlayerPerformanceView } from './SyntheticIndividualPlayerPerformanceView'
import { AthleteFichaDrawer } from './AthleteFichaDrawer'
import {
  Activity,
  Trophy,
  User,
  Sparkles,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
} from 'lucide-react'

export type ActiveExperience = 'tracking' | 'sport' | 'individual' | 'metrics_viability'
export type SimulatedRole = 'medic' | 'coach' | 'athlete'

interface DashboardPreviewViewProps {
  onNavigateToEvaluations?: () => void
  onNavigateToHealth?: () => void
  onNavigateToStats?: () => void
  onBackToApp?: () => void
}

export const DashboardPreviewView: React.FC<DashboardPreviewViewProps> = ({
  onNavigateToEvaluations,
  onNavigateToHealth,
  onNavigateToStats,
  onBackToApp,
}) => {
  let user = null
  try {
    const auth = useAuth()
    user = auth?.user ?? null
  } catch {
    // Isolated preview or test without AuthProvider context
    user = null
  }

  const [activeExp, setActiveExp] = useState<ActiveExperience>('tracking')
  const [simulatedRole, setSimulatedRole] = useState<SimulatedRole>('medic')
  const [selectedAthleteForFicha, setSelectedAthleteForFicha] = useState<SyntheticAthlete | null>(null)

  const isCoachView = simulatedRole === 'coach'
  const isAthleteView = simulatedRole === 'athlete'

  return (
    <div className="dashboard-preview-root" style={{
      padding: '1rem',
      maxWidth: '1380px',
      margin: '0 auto',
      minHeight: '100vh',
      fontFamily: 'inherit',
    }}>
      {/* Top Banner: Preview notice, Experiences Switcher & Role Simulator */}
      <div style={{
        background: 'var(--nav-bg-base, #0a1320)',
        color: '#ffffff',
        borderRadius: '10px',
        padding: '1.25rem 1.5rem',
        marginBottom: '1.25rem',
        boxShadow: '0 4px 16px rgba(10, 19, 32, 0.25)',
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{
                background: 'var(--brand-red, #b91c1c)',
                color: '#ffffff',
                fontSize: '0.72rem',
                fontWeight: 800,
                padding: '2px 8px',
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}>
                M8C · Propuesta Visual Refinada
              </span>
              <span style={{ fontSize: '0.78rem', color: 'var(--nav-text-muted, #94a3b8)' }}>
                Fase de elección · Datos sintéticos deterministas (Sin conexión BD){user?.email ? ` · Sesión: ${user.email}` : ''}
              </span>
              <a
                href="/?view=dashboard"
                style={{
                  background: 'var(--brand-yellow, #eab308)',
                  color: '#0a1320',
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  textDecoration: 'none',
                  marginLeft: '8px',
                }}
              >
                Abrir Dashboard Operativo (Datos Reales) ➔
              </a>
            </div>

            <h1 style={{
              margin: 0,
              fontSize: '1.4rem',
              fontWeight: 800,
              color: '#ffffff',
              letterSpacing: '-0.02em',
            }}>
              Dashboard Integrado de Seguimiento y Rendimiento
            </h1>
          </div>

          {/* Navigation Controls: Experiences Tabs */}
          <div style={{
            display: 'inline-flex',
            background: 'var(--nav-bg-surface, #101e33)',
            border: '1px solid var(--nav-border, #1a2d4a)',
            borderRadius: '8px',
            padding: '3px',
            flexWrap: 'wrap',
          }}>
            <button
              type="button"
              onClick={() => setActiveExp('tracking')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                borderRadius: '6px',
                border: 'none',
                background: activeExp === 'tracking' ? 'var(--brand-red, #b91c1c)' : 'transparent',
                color: '#ffffff',
                fontSize: '0.82rem',
                fontWeight: activeExp === 'tracking' ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <Activity size={15} />
              <span>A. Seguimiento del Equipo</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveExp('sport')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                borderRadius: '6px',
                border: 'none',
                background: activeExp === 'sport' ? 'var(--brand-red, #b91c1c)' : 'transparent',
                color: '#ffffff',
                fontSize: '0.82rem',
                fontWeight: activeExp === 'sport' ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <Trophy size={15} />
              <span>B. Rendimiento Deportivo</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveExp('individual')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 14px',
                borderRadius: '6px',
                border: 'none',
                background: activeExp === 'individual' ? 'var(--brand-red, #b91c1c)' : 'transparent',
                color: '#ffffff',
                fontSize: '0.82rem',
                fontWeight: activeExp === 'individual' ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <User size={15} />
              <span>C. Rendimiento Individual</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveExp('metrics_viability')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '7px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeExp === 'metrics_viability' ? '#1e293b' : 'transparent',
                color: activeExp === 'metrics_viability' ? '#f8fafc' : 'var(--nav-text-muted, #94a3b8)',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <Sparkles size={14} color="#f59e0b" />
              <span>Métricas Nuevas</span>
            </button>
          </div>
        </div>

        {/* Sub-strip: Simulated Role selector & Back to App button */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginTop: '1rem',
          paddingTop: '0.85rem',
          borderTop: '1px solid var(--nav-border, #1a2d4a)',
          fontSize: '0.8rem',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'var(--nav-text-muted, #94a3b8)', fontWeight: 600 }}>
              Simular perspectiva de rol:
            </span>

            <div style={{
              display: 'inline-flex',
              background: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '6px',
              padding: '2px',
            }}>
              <button
                type="button"
                onClick={() => setSimulatedRole('medic')}
                style={{
                  padding: '3px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  background: simulatedRole === 'medic' ? '#ffffff' : 'transparent',
                  color: simulatedRole === 'medic' ? '#0a1320' : '#ffffff',
                  fontSize: '0.75rem',
                  fontWeight: simulatedRole === 'medic' ? 800 : 500,
                  cursor: 'pointer',
                }}
              >
                Staff / Sanitario (Todo autorizado)
              </button>
              <button
                type="button"
                onClick={() => setSimulatedRole('coach')}
                style={{
                  padding: '3px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  background: simulatedRole === 'coach' ? '#ffffff' : 'transparent',
                  color: simulatedRole === 'coach' ? '#0a1320' : '#ffffff',
                  fontSize: '0.75rem',
                  fontWeight: simulatedRole === 'coach' ? 800 : 500,
                  cursor: 'pointer',
                }}
              >
                Coach Deportivo (Salud privada restringida)
              </button>
              <button
                type="button"
                onClick={() => setSimulatedRole('athlete')}
                style={{
                  padding: '3px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  background: simulatedRole === 'athlete' ? '#ffffff' : 'transparent',
                  color: simulatedRole === 'athlete' ? '#0a1320' : '#ffffff',
                  fontSize: '0.75rem',
                  fontWeight: simulatedRole === 'athlete' ? 800 : 500,
                  cursor: 'pointer',
                }}
              >
                Jugador (Solo estadísticas propias)
              </button>
            </div>
          </div>

          {onBackToApp && (
            <button
              type="button"
              onClick={onBackToApp}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '6px',
                background: 'transparent',
                border: '1px solid var(--nav-border, #1a2d4a)',
                color: 'var(--nav-text-muted, #94a3b8)',
                fontSize: '0.78rem',
                cursor: 'pointer',
              }}
            >
              <ArrowLeft size={13} />
              <span>Volver a la aplicación</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Experience Host */}
      {activeExp === 'tracking' && (
        <SyntheticTeamTrackingView
          onSelectAthlete={(ath) => setSelectedAthleteForFicha(ath)}
          onNavigateToHealth={onNavigateToHealth}
          onNavigateToStats={onNavigateToStats}
          isCoachView={isCoachView}
        />
      )}

      {activeExp === 'sport' && (
        <SyntheticSportPerformanceView
          onNavigateToStats={onNavigateToStats}
        />
      )}

      {activeExp === 'individual' && (
        <SyntheticIndividualPlayerPerformanceView
          onOpenDrawer={(ath) => setSelectedAthleteForFicha(ath)}
          isAthleteView={isAthleteView}
          initialAthleteId="ath-01"
        />
      )}

      {activeExp === 'metrics_viability' && (
        <div style={{
          background: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '10px',
          padding: '1.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
        }}>
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={18} color="#d97706" />
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                Evaluación Técnica de Métricas Nuevas Propuestas (M8C Punto 7)
              </h2>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.82rem', color: 'var(--text-secondary, #475569)' }}>
              Análisis de viabilidad matemática y contractual con los datos disponibles en la consola de campo existente sin requerir cambios invasivos.
            </p>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
              <thead style={{ background: 'var(--surface-subtle, #f8fafc)', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b' }}>Métrica</th>
                  <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b' }}>Fórmula / Algoritmo</th>
                  <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b' }}>Datos Requeridos</th>
                  <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b' }}>Estado</th>
                  <th style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b' }}>Evaluación de Viabilidad</th>
                </tr>
              </thead>
              <tbody>
                {PROPOSED_METRICS_EVALUATION.map((m) => (
                  <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#0f172a' }}>{m.name}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '0.78rem', color: '#475569' }}>{m.formula}</td>
                    <td style={{ padding: '10px 12px', color: '#64748b' }}>{m.requiredData}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        background: m.status === 'disponible' ? '#dcfce7' : m.status === 'parcialmente_disponible' ? '#fef3c7' : '#fee2e2',
                        color: m.status === 'disponible' ? '#15803d' : m.status === 'parcialmente_disponible' ? '#b45309' : '#b91c1c',
                      }}>
                        {m.status === 'disponible' ? (
                          <>
                            <CheckCircle2 size={12} />
                            <span>Disponible</span>
                          </>
                        ) : m.status === 'parcialmente_disponible' ? (
                          <>
                            <AlertCircle size={12} />
                            <span>Parcial</span>
                          </>
                        ) : (
                          <>
                            <HelpCircle size={12} />
                            <span>No disponible con registro actual</span>
                          </>
                        )}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#334155', fontSize: '0.78rem' }}>{m.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transversal Athlete Ficha Drawer */}
      <AthleteFichaDrawer
        athlete={selectedAthleteForFicha}
        onClose={() => setSelectedAthleteForFicha(null)}
        onNavigateToEvaluations={onNavigateToEvaluations}
        onNavigateToHealth={onNavigateToHealth}
      />
    </div>
  )
}
